import {
  createBuilding,
  getAllBuildings,
  getAllLockProviders,
  getMembersByBuildingId,
  uploadMembersByBuildingId,
  deleteBuilding,
  updateBuilding,
  getBuildingByBuildingId,
  clearGetBuildingByBuildingId,
  getBuildingsByGroupId,
  uploadExistingMembersByBuildingId,
} from "./BuildingActions";
import { addMember } from "../Members/MembersActions";
import { createSlice } from "@reduxjs/toolkit";

interface BookingsState {
  buildings:
    | {
        address: string;
        amenigiesShare: number;
        amenitiesCount: number;
        buildingId: number;
        buildingName: string;
        buildingStatus: string;
        city: string;
        groupId: number;
        image: string;
        lat: number;
        long: number;
        ownerShare: number;
        password: string;
        terms: string;
        username: string;
        zip: string;
        state: string;
        groupName: string;
      }[]
    | null;
  buildingsLoading: boolean;
  buildingsError: any | null;

  lockProviders: { id: number; name: string }[] | null;
  lockProvidersLoading: boolean;
  lockProvidersError: any | null;

  createBuildingLoading: boolean;
  createBuildingError: any | null;
  createBuilding: any | null;

  addMemberLoading: boolean;
  addMemberError: any | null;
  addMember: any | null;

  getMembersByBuildingIdLoading: boolean;
  getMembersByBuildingIdError: any | null;
  getMembersByBuildingIdList: any[] | null;

  uploadMembersLoading: boolean;
  uploadMembersError: any | null;
  uploadMembers: any | null;

  deleteBuildingLoading: boolean;
  deleteBuildingError: any | null;
  deleteBuilding: any | null;

  updateBuildingLoading: boolean;
  updateBuildingError: any | null;
  updateBuilding: any | null;

  getBuildingByIdLoading: boolean;
  getBuildingByIdError: any | null;
  getBuildingById: any | null;

  getBuildingsByGroupIdLoading: boolean;
  getBuildingsByGroupIdError: any | null;
  getBuildingsByGroupIdList: any[] | null;

  uploadExistingMembersLoading: boolean;
  uploadExistingMembersError: any | null;
  uploadExistingMembersData: any | null;
}

const initialState: BookingsState = {
  buildings: null,
  buildingsLoading: false,
  buildingsError: null,

  lockProviders: null,
  lockProvidersLoading: false,
  lockProvidersError: null,

  createBuildingLoading: false,
  createBuildingError: null,
  createBuilding: null,

  getMembersByBuildingIdLoading: false,
  getMembersByBuildingIdError: null,
  getMembersByBuildingIdList: null,

  addMemberLoading: false,
  addMemberError: null,
  addMember: null,

  uploadMembersLoading: false,
  uploadMembersError: null,
  uploadMembers: null,

  deleteBuildingLoading: false,
  deleteBuildingError: null,
  deleteBuilding: null,

  updateBuildingLoading: false,
  updateBuildingError: null,
  updateBuilding: null,

  getBuildingByIdLoading: false,
  getBuildingByIdError: null,
  getBuildingById: null,

  getBuildingsByGroupIdLoading: false,
  getBuildingsByGroupIdError: null,
  getBuildingsByGroupIdList: null,

  uploadExistingMembersLoading: false,
  uploadExistingMembersError: null,
  uploadExistingMembersData: null,
};

const buildingsSlice = createSlice({
  name: "buildings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllBuildings.pending, (state) => {
      state.buildingsLoading = true;
      state.buildingsError = null;
      state.buildings = null;
    });
    builder.addCase(getAllBuildings.fulfilled, (state, action) => {
      state.buildingsLoading = false;
      state.buildings = action.payload;
      state.buildingsError = null;
    });
    builder.addCase(getAllBuildings.rejected, (state, action) => {
      state.buildingsLoading = false;
      state.buildingsError = action.payload;
      state.buildings = null;
    });

    builder.addCase(getAllLockProviders.pending, (state) => {
      state.lockProvidersLoading = true;
      state.lockProvidersError = null;
      state.lockProviders = null;
    });

    builder.addCase(getAllLockProviders.fulfilled, (state, action) => {
      state.lockProvidersLoading = false;
      state.lockProviders = action.payload;
      state.lockProvidersError = null;
    });

    builder.addCase(getAllLockProviders.rejected, (state, action) => {
      state.lockProvidersLoading = false;
      state.lockProvidersError = action.payload;
      state.lockProviders = null;
    });

    builder.addCase(createBuilding.pending, (state) => {
      state.createBuildingLoading = true;
      state.createBuildingError = null;
    });
    builder.addCase(createBuilding.fulfilled, (state, action) => {
      state.createBuildingLoading = false;
      state.createBuildingError = null;
      state.createBuilding = action.payload;
    });

    builder.addCase(createBuilding.rejected, (state, action) => {
      state.createBuildingLoading = false;
      state.createBuildingError = action.payload;
      state.createBuilding = null;
    });

    builder.addCase(addMember.pending, (state) => {
      state.addMemberLoading = true;
      state.addMemberError = null;
      state.addMember = null;
    });

    builder.addCase(addMember.fulfilled, (state, action) => {
      state.addMemberLoading = false;
      state.addMemberError = null;
      state.addMember = action.payload;
    });

    builder.addCase(addMember.rejected, (state, action) => {
      state.addMemberLoading = false;
      state.addMemberError = action.payload;
      state.addMember = null;
    });

    builder.addCase(getMembersByBuildingId.pending, (state) => {
      state.getMembersByBuildingIdLoading = true;
      state.getMembersByBuildingIdError = null;
      state.getMembersByBuildingIdList = null;
    });

    builder.addCase(getMembersByBuildingId.fulfilled, (state, action) => {
      state.getMembersByBuildingIdLoading = false;
      state.getMembersByBuildingIdError = null;
      state.getMembersByBuildingIdList = action.payload;
    });

    builder.addCase(getMembersByBuildingId.rejected, (state, action) => {
      state.getMembersByBuildingIdLoading = false;
      state.getMembersByBuildingIdError = action.payload;
      state.getMembersByBuildingIdList = null;
    });

    builder.addCase(uploadMembersByBuildingId.pending, (state) => {
      state.uploadMembersLoading = true;
      state.uploadMembersError = null;
      state.uploadMembers = null;
    });

    builder.addCase(uploadMembersByBuildingId.fulfilled, (state, action) => {
      state.uploadMembersLoading = false;
      state.uploadMembersError = null;
      state.uploadMembers = action.payload;
    });

    builder.addCase(uploadMembersByBuildingId.rejected, (state, action) => {
      state.uploadMembersLoading = false;
      state.uploadMembersError = action.payload;
      state.uploadMembers = null;
    });

    builder.addCase(deleteBuilding.pending, (state) => {
      state.deleteBuildingLoading = true;
      state.deleteBuildingError = null;
      state.deleteBuilding = null;
    });

    builder.addCase(deleteBuilding.fulfilled, (state, action) => {
      state.deleteBuildingLoading = false;
      state.deleteBuildingError = null;
      state.deleteBuilding = action.payload;
    });

    builder.addCase(deleteBuilding.rejected, (state, action) => {
      state.deleteBuildingLoading = false;
      state.deleteBuildingError = action.payload;
      state.deleteBuilding = null;
    });

    builder.addCase(updateBuilding.pending, (state) => {
      state.updateBuildingLoading = true;
      state.updateBuildingError = null;
      state.updateBuilding = null;
    });

    builder.addCase(updateBuilding.fulfilled, (state, action) => {
      state.updateBuildingLoading = false;
      state.updateBuildingError = null;
      state.updateBuilding = action.payload;
    });

    builder.addCase(updateBuilding.rejected, (state, action) => {
      state.updateBuildingLoading = false;
      state.updateBuildingError = action.payload;
      state.updateBuilding = null;
    });

    builder.addCase(getBuildingByBuildingId.pending, (state) => {
      state.getBuildingByIdLoading = true;
      state.getBuildingByIdError = null;
      state.getBuildingById = null;
    });

    builder.addCase(getBuildingByBuildingId.fulfilled, (state, action) => {
      state.getBuildingByIdLoading = false;
      state.getBuildingByIdError = null;
      state.getBuildingById = action.payload;
    });

    builder.addCase(getBuildingByBuildingId.rejected, (state, action) => {
      state.getBuildingByIdLoading = false;
      state.getBuildingByIdError = action.payload;
      state.getBuildingById = null;
    });

    builder.addCase(clearGetBuildingByBuildingId.fulfilled, (state) => {
      state.getBuildingByIdLoading = false;
      state.getBuildingByIdError = null;
      state.getBuildingById = null;
    });

    builder.addCase(getBuildingsByGroupId.pending, (state) => {
      state.getBuildingsByGroupIdLoading = true;
      state.getBuildingsByGroupIdError = null;
      state.getBuildingsByGroupIdList = null;
    });

    builder.addCase(getBuildingsByGroupId.fulfilled, (state, action) => {
      state.getBuildingsByGroupIdLoading = false;
      state.getBuildingsByGroupIdError = null;
      state.getBuildingsByGroupIdList = action.payload;
    });

    builder.addCase(getBuildingsByGroupId.rejected, (state, action) => {
      state.getBuildingsByGroupIdLoading = false;
      state.getBuildingsByGroupIdError = action.payload;
      state.getBuildingsByGroupIdList = null;
    });

    builder.addCase(uploadExistingMembersByBuildingId.pending, (state) => {
      state.uploadExistingMembersLoading = true;
      state.uploadExistingMembersError = null;
      state.uploadExistingMembersData = null;
    });

    builder.addCase(
      uploadExistingMembersByBuildingId.fulfilled,
      (state, action) => {
        state.uploadExistingMembersLoading = false;
        state.uploadExistingMembersError = null;
        state.uploadExistingMembersData = action.payload;
      },
    );

    builder.addCase(
      uploadExistingMembersByBuildingId.rejected,
      (state, action) => {
        state.uploadExistingMembersLoading = false;
        state.uploadExistingMembersError = action.payload;
        state.uploadExistingMembersData = null;
      },
    );
  },
});

const buildingsReducer = buildingsSlice.reducer;

export default buildingsReducer;
